// src/pages/admin/FeaturedModelsManagement.tsx
import { useState, useEffect, useCallback, useMemo } from 'react'; // Added useMemo
import { AdminLayout } from '../../components/AdminLayout';
import { carPricesApi, CarModelWithBrand } from '../../lib/carPricesApi';
// Added Search icon below
import { Star, FileText, AlertCircle, X, Check, Edit2, Trash2, Search, FilterX } from 'lucide-react';

// Helper function kept outside component
const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

export function FeaturedModelsManagement() {
  // --- EXISTING STATE ---
  const [models, setModels] = useState<CarModelWithBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<CarModelWithBrand | null>(null);
  
  // State for characteristics import modal (kept as is)
  const [showCharacteristicsModal, setShowCharacteristicsModal] = useState(false);
  const [characteristicsText, setCharacteristicsText] = useState('');
  const [parsedCharacteristics, setParsedCharacteristics] = useState<Record<string, any>>({});
  const [parseError, setParseError] = useState<string | null>(null);
  const [editingCharacteristic, setEditingCharacteristic] = useState<{key: string, value: string} | null>(null);
  const [tempCharacteristicKey, setTempCharacteristicKey] = useState('');
  const [tempCharacteristicValue, setTempCharacteristicValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // const [showSpecifications, setShowSpecifications] = useState(false); // Unused state removed for cleanliness

  // --- NEW FILTER STATE ---
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'featured' | 'not_featured'>('all');

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    try {
      setLoading(true);
      const modelsData = await carPricesApi.getModels();
      setModels(modelsData);
    } catch (error) {
      console.error('Error loading models:', error);
      setError('Failed to load models');
    } finally {
      setLoading(false);
    }
  };

  // --- FILTERING LOGIC ---

  // 1. Get unique brand names for the dropdown menu based on loaded data
  const uniqueBrands = useMemo(() => {
    const brands = new Set(models.map(model => model.brand?.name).filter(Boolean));
    return Array.from(brands).sort();
  }, [models]);

  // 2. Apply filters to the models list
  const filteredModels = useMemo(() => {
    return models.filter(model => {
      // Filter by Search Term (Model Name)
      const matchesSearch = model.name.toLowerCase().includes(searchTerm.toLowerCase());

      // Filter by Brand
      const matchesBrand = selectedBrand === 'all' || model.brand?.name === selectedBrand;

      // Filter by Status (Featured/Not Featured)
      let matchesStatus = true;
      if (statusFilter === 'featured') {
        matchesStatus = model.is_featured === true;
      } else if (statusFilter === 'not_featured') {
        matchesStatus = !model.is_featured;
      }

      return matchesSearch && matchesBrand && matchesStatus;
    });
  }, [models, searchTerm, selectedBrand, statusFilter]);

  // Function to reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedBrand('all');
    setStatusFilter('all');
  };

  // --- EXISTING FUNCTIONS (kept as is) ---
  const toggleFeatured = async (modelId: string, isFeatured: boolean) => {
    try {
      const currentModel = models.find(m => m.id === modelId);
      if (!currentModel) {
        alert('Modèle non trouvé');
        return;
      }
      
      await carPricesApi.updateModel(modelId, { 
        slug: currentModel.slug || generateSlug(currentModel.name),
        is_featured: !isFeatured 
      });
      await loadModels();
    } catch (error) {
      console.error('Error updating model:', error);
      alert('Error updating model');
    }
  };

  // ... (keep parseCharacteristics, handleApplyCharacteristics, handleEditCharacteristic, 
  // handleSaveEditedCharacteristic, handleDeleteCharacteristic, handleAddCharacteristic,
  // openCharacteristicsModal, closeCharacteristicsModal exactly the same)

    // Function to parse characteristics
    const parseCharacteristics = useCallback(() => {
      try {
        setParseError(null);
        const lines = characteristicsText.split('\n');
        const result: Record<string, any> = {};
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          
          // Skip empty lines
          if (!line) continue;
          
          // Check if it's a key-value pair (contains tabs)
          if (line.includes('\t')) {
            const parts = line.split('\t');
            const key = parts[0].trim();
            const value = parts.slice(1).join('\t').trim();
            
            if (key && value) {
              // Convert key to a more database-friendly format
              const dbKey = key
                .toLowerCase()
                .replace(/[^a-z0-9]/g, '_')
                .replace(/_+/g, '_')
                .replace(/^_|_$/g, '');
              
              result[dbKey] = value;
            }
          }
        }
        
        if (Object.keys(result).length === 0) {
          setParseError("Aucune caractéristique trouvée. Vérifiez que le format est correct avec des tabulations entre les clés et les valeurs.");
          return;
        }
        
        setParsedCharacteristics(result);
      } catch (error) {
        console.error('Error parsing characteristics:', error);
        setParseError("Une erreur s'est produite lors de l'analyse des caractéristiques. Vérifiez le format et réessayez.");
      }
    }, [characteristicsText]);
  
    // Function to apply characteristics to the selected model
    const handleApplyCharacteristics = useCallback(async () => {
      if (!selectedModel) return;
      
      try {
        setSubmitting(true);
        
        // Get the current model data
        const currentModel = models.find(m => m.id === selectedModel.id);
        if (!currentModel) {
          alert('Modèle non trouvé');
          return;
        }
        
        // Update the model with new characteristics and ensure slug is included
        await carPricesApi.updateModel(selectedModel.id, {
          slug: currentModel.slug || generateSlug(currentModel.name), // Ensure slug is included
          specifications: {
            ...currentModel.specifications,
            ...parsedCharacteristics
          }
        });
        
        // Reload models to get updated data
        await loadModels();
        
        // Close the modal and reset state
        setShowCharacteristicsModal(false);
        setCharacteristicsText('');
        setParsedCharacteristics({});
        setParseError(null);
        setEditingCharacteristic(null);
        setTempCharacteristicKey('');
        setTempCharacteristicValue('');
        
        alert('Caractéristiques importées avec succès');
      } catch (error) {
        console.error('Error applying characteristics:', error);
        alert('Erreur lors de l\'application des caractéristiques');
      } finally {
        setSubmitting(false);
      }
    }, [selectedModel, models, parsedCharacteristics]);
  
    // Function to edit a characteristic
    const handleEditCharacteristic = useCallback((key: string, value: string) => {
      setEditingCharacteristic({key, value});
      setTempCharacteristicKey(key);
      setTempCharacteristicValue(value);
    }, []);
  
    // Function to save edited characteristic
    const handleSaveEditedCharacteristic = useCallback(() => {
      if (editingCharacteristic) {
        const updatedCharacteristics = {...parsedCharacteristics};
        
        // Remove the old key if it changed
        if (editingCharacteristic.key !== tempCharacteristicKey) {
          delete updatedCharacteristics[editingCharacteristic.key];
        }
        
        // Add the new key-value pair
        updatedCharacteristics[tempCharacteristicKey] = tempCharacteristicValue;
        
        setParsedCharacteristics(updatedCharacteristics);
        setEditingCharacteristic(null);
        setTempCharacteristicKey('');
        setTempCharacteristicValue('');
      }
    }, [editingCharacteristic, parsedCharacteristics, tempCharacteristicKey, tempCharacteristicValue]);
  
    // Function to delete a characteristic
    const handleDeleteCharacteristic = useCallback((key: string) => {
      const updatedCharacteristics = {...parsedCharacteristics};
      delete updatedCharacteristics[key];
      setParsedCharacteristics(updatedCharacteristics);
    }, [parsedCharacteristics]);
  
    // Function to add a new characteristic
    const handleAddCharacteristic = useCallback(() => {
      if (tempCharacteristicKey && tempCharacteristicValue) {
        const updatedCharacteristics = {
          ...parsedCharacteristics,
          [tempCharacteristicKey]: tempCharacteristicValue
        };
        setParsedCharacteristics(updatedCharacteristics);
        setTempCharacteristicKey('');
        setTempCharacteristicValue('');
      }
    }, [tempCharacteristicKey, tempCharacteristicValue, parsedCharacteristics]);
  
    // Function to open the characteristics modal for a specific model
    const openCharacteristicsModal = useCallback((model: CarModelWithBrand) => {
      setSelectedModel(model);
      setShowCharacteristicsModal(true);
      // Initialize with current specifications if they exist
      if (model.specifications) {
        setParsedCharacteristics(model.specifications);
      }
    }, []);
  
    // Function to close the characteristics modal
    const closeCharacteristicsModal = useCallback(() => {
      setShowCharacteristicsModal(false);
      setSelectedModel(null);
      setCharacteristicsText('');
      setParsedCharacteristics({});
      setParseError(null);
      setEditingCharacteristic(null);
      setTempCharacteristicKey('');
      setTempCharacteristicValue('');
    }, []);


  return (
    <AdminLayout currentPage="featured-models">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Modèles Vedettes</h1>
          <p className="text-gray-600 text-sm mt-1">
            Choisissez les modèles à afficher sur la page d'accueil
          </p>
        </div>

        {/* --- FILTERS SECTION START --- */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto flex-1">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Rechercher un modèle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-[#D4452C] focus:border-[#D4452C] sm:text-sm transition duration-150 ease-in-out"
              />
            </div>

            {/* Brand Filter Dropdown */}
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="block w-full sm:w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#D4452C] focus:border-[#D4452C] sm:text-sm rounded-md"
            >
              <option value="all">Toutes les marques</option>
              {uniqueBrands.map((brandName) => (
                <option key={brandName} value={brandName}>
                  {brandName}
                </option>
              ))}
            </select>

            {/* Status Filter Dropdown */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'featured' | 'not_featured')}
              className="block w-full sm:w-48 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-[#D4452C] focus:border-[#D4452C] sm:text-sm rounded-md"
            >
              <option value="all">Tous les statuts</option>
              <option value="featured">Vedettes uniquement</option>
              <option value="not_featured">Non vedettes</option>
            </select>
          </div>
          
          {/* Reset Filter Button */}
          {(searchTerm || selectedBrand !== 'all' || statusFilter !== 'all') && (
             <button
               onClick={resetFilters}
               className="flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4452C] transition-colors"
             >
               <FilterX className="h-4 w-4 mr-2" />
               Réinitialiser
             </button>
          )}
        </div>
        {/* --- FILTERS SECTION END --- */}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4452C]"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">{error}</p>
            <button
              onClick={loadModels}
              className="mt-4 px-4 py-2 bg-[#D4452C] text-white rounded-lg hover:bg-[#b83a24]"
            >
              Réessayer
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Modèle ({filteredModels.length})
                    </th>
                    {/* ... other headers ... */}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Marque
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prix
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* CHANGED: Map over filteredModels instead of models */}
                  {filteredModels.length > 0 ? (
                    filteredModels.map((model) => (
                    <tr key={model.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {model.images && model.images.length > 0 ? (
                            <img
                              src={model.images[0]}
                              alt={model.name}
                              className="h-10 w-10 rounded-full object-cover mr-3"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                              <span className="text-gray-500 text-xs">No img</span>
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{model.name}</div>
                            <div className="text-xs text-gray-500">
                              {model.engine && <span>{model.engine}</span>}
                              {model.transmission && <span> • {model.transmission}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {model.brand?.name || 'Unknown'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {model.starting_price ? `${model.starting_price.toLocaleString()} TND` : 'Prix sur demande'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          model.is_featured
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {model.is_featured ? (
                            <>
                              <Star className="w-3 h-3 mr-1" />
                              Vedette
                            </>
                          ) : (
                            'Normal'
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {/* Only show the specifications button if the model doesn't have multiple versions */}
                          {(!model.versions || model.versions.length <= 1) && (
                            <button
                              onClick={() => openCharacteristicsModal(model)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Importer des caractéristiques"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => toggleFeatured(model.id, model.is_featured || false)}
                            className={`p-2 rounded-lg transition-colors ${
                              model.is_featured
                                ? 'text-yellow-600 hover:bg-yellow-50'
                                : 'text-gray-400 hover:bg-gray-100'
                            }`}
                            title={model.is_featured ? 'Retirer des vedettes' : 'Ajouter aux vedettes'}
                          >
                            <Star className={`h-4 w-4 ${model.is_featured ? 'fill-current' : ''}`} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                        <div className="flex flex-col items-center justify-center">
                          <Search className="h-10 w-10 text-gray-300 mb-2" />
                          <p className="text-lg font-medium text-gray-900">Aucun résultat trouvé</p>
                          <p className="text-sm text-gray-500">Essayez de modifier vos filtres de recherche.</p>
                          <button onClick={resetFilters} className="mt-4 text-[#D4452C] hover:underline">
                             Réinitialiser les filtres
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal for Characteristics Import (Kept exactly the same) */}
        {showCharacteristicsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            {/* ... modal content omitted for brevity as it didn't change ... */}
             <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">
                  Importer des caractéristiques pour {selectedModel?.name}
                </h2>
                <button
                  onClick={closeCharacteristicsModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Collez les caractéristiques du modèle ci-dessous. Le système les analysera automatiquement et les organisera dans les champs appropriés.
                  </p>
                  <p className="text-xs text-gray-500">
                    Assurez-vous que le format est similaire à l'exemple fourni, avec des catégories et des paires clé-valeur séparées par des tabulations.
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Caractéristiques du modèle
                  </label>
                  <textarea
                    value={characteristicsText}
                    onChange={(e) => setCharacteristicsText(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                    rows={15}
                    placeholder="Collez les caractéristiques ici..."
                  />
                </div>
                {parseError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-800">{parseError}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={parseCharacteristics}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Analyser les caractéristiques
                  </button>
                  {Object.keys(parsedCharacteristics).length > 0 && (
                    <button
                      type="button"
                      onClick={handleApplyCharacteristics}
                      disabled={submitting}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center disabled:opacity-50"
                    >
                      {submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Application en cours...
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Appliquer au modèle
                        </>
                      )}
                    </button>
                  )}
                </div>

                {Object.keys(parsedCharacteristics).length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">Caractéristiques analysées</h3>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={tempCharacteristicKey}
                          onChange={(e) => setTempCharacteristicKey(e.target.value)}
                          placeholder="Nouvelle clé"
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                        />
                        <input
                          type="text"
                          value={tempCharacteristicValue}
                          onChange={(e) => setTempCharacteristicValue(e.target.value)}
                          placeholder="Nouvelle valeur"
                          className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                        />
                        <button
                          type="button"
                          onClick={handleAddCharacteristic}
                          className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
                        >
                          Ajouter
                        </button>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Caractéristique
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Valeur
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {Object.entries(parsedCharacteristics).map(([key, value]) => (
                            <tr key={key}>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {editingCharacteristic?.key === key ? (
                                  <input
                                    type="text"
                                    value={tempCharacteristicKey}
                                    onChange={(e) => setTempCharacteristicKey(e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                                  />
                                ) : (
                                  key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                )}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-700">
                                {editingCharacteristic?.key === key ? (
                                  <input
                                    type="text"
                                    value={tempCharacteristicValue}
                                    onChange={(e) => setTempCharacteristicValue(e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#D4452C]"
                                  />
                                ) : (
                                  value
                                )}
                              </td>
                              <td className="px-4 py-2 text-right text-sm font-medium">
                                {editingCharacteristic?.key === key ? (
                                  <div className="flex justify-end space-x-1">
                                    <button
                                      type="button"
                                      onClick={handleSaveEditedCharacteristic}
                                      className="p-1 text-green-600 hover:bg-green-50 rounded"
                                    >
                                      <Check className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingCharacteristic(null);
                                        setTempCharacteristicKey('');
                                        setTempCharacteristicValue('');
                                      }}
                                      className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex justify-end space-x-1">
                                    <button
                                      type="button"
                                      onClick={() => handleEditCharacteristic(key, value)}
                                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteCharacteristic(key)}
                                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}